import React from "react";

// ui
import { IconButton, Box, Typography } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

// ui rechart
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

// jotai
import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";
import { dataDifine } from "../config/dataInterface";

const colorPlette = [
  "#FF4B00",
  "#005AFF",
  "#03AF7A",
  "#4DC4FF",
  "#F6AA00",
  "#FFF100",
  "#990099",
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: any; // データの型によって調整
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  const isVisible = active && payload && payload.length;
  return (
    <div
      className="custom-tooltip"
      style={{
        margin: "0px",
        padding: "10px",
        backgroundColor: "rgb(255, 255, 255)",
        border: "1px solid rgb(204, 204, 204)",
        whiteSpace: "nowrap",
      }}
    >
      {isVisible && (
        <>
          <p style={{ margin: "5px" }}>{`${label}`}</p>
          {payload.map((_payload: any) => {
            if (_payload.value !== 0) {
              if (
                _payload.payload[_payload.name + "_sub_column"] === undefined ||
                _payload.payload[_payload.name + "_sub_column"] === 0
              ) {
                return (
                  <p
                    style={{ color: _payload.color, margin: "5px" }}
                  >{`${_payload.name} : ${_payload.value}`}</p>
                );
              } else {
                return (
                  <p style={{ color: _payload.color, margin: "5px" }}>{`${
                    _payload.name
                  } : ${_payload.value}（${
                    _payload.payload["sub_count_column_name"]
                  }：${_payload.payload[_payload.name + "_sub_column"]}）`}</p>
                );
              }
            }
          })}
        </>
      )}
    </div>
  );
};

const CustomTooltipAll: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  const isVisible = active && payload && payload.length;
  return (
    <div
      className="custom-tooltip"
      style={{
        margin: "0px",
        padding: "10px",
        backgroundColor: "rgb(255, 255, 255)",
        border: "1px solid rgb(204, 204, 204)",
        whiteSpace: "nowrap",
      }}
    >
      {isVisible && (
        <>
          <p style={{ margin: "5px" }}>{`${label}`}</p>
          {payload.map((_payload: any) => {
            if (_payload.value !== 0) {
              if (
                _payload.payload["サブ"] === undefined ||
                _payload.payload["サブ"] === 0
              ) {
                return (
                  <p
                    style={{ color: _payload.color, margin: "5px" }}
                  >{`${_payload.name} : ${_payload.value}`}</p>
                );
              } else {
                return (
                  <p
                    style={{ color: _payload.color, margin: "5px" }}
                  >{`${_payload.name} : ${_payload.value}（${_payload.payload["sub_count_column_name"]}：${_payload.payload["サブ"]}）`}</p>
                );
              }
            }
          })}
        </>
      )}
    </div>
  );
};

/** 全角1文字あたりの概算ピクセル幅（fontSize 12） */
const FULL_WIDTH_PX = 12;
/** 半角1文字あたりの概算ピクセル幅 */
const HALF_WIDTH_PX = 6;

function isHalfWidthChar(c: string): boolean {
  const code = c.charCodeAt(0);
  return code < 128 || (code >= 0xff61 && code <= 0xff9f);
}

/** 指定ピクセル幅を超えないように改行（半角は幅半分として計算） */
function splitLabelByWidth(text: string, maxWidthPx: number): string[] {
  const str = typeof text === "string" ? text : String(text ?? "");
  if (maxWidthPx <= 0 || !str) return str ? [str] : [];
  const lines: string[] = [];
  let current = "";
  let currentPx = 0;
  for (const c of str) {
    const w = isHalfWidthChar(c) ? HALF_WIDTH_PX : FULL_WIDTH_PX;
    if (currentPx + w > maxWidthPx && current.length > 0) {
      lines.push(current);
      current = "";
      currentPx = 0;
    }
    current += c;
    currentPx += w;
  }
  if (current) lines.push(current);
  return lines;
}

/** 1行の高さ（px）。fontSizeに合わせて調整 */
function lineHeightForFontSize(fontSize: number): number {
  return Math.max(14, fontSize + 4);
}

export const CustomizedYAxisTick = (props: {
  x?: number;
  y?: number;
  payload?: { value?: string };
  width?: number;
  /** ラベル領域の幅（px）。指定時はこの幅をはみ出さないように改行。詳細モーダルと揃えるなら 92 以上推奨 */
  labelWidth?: number;
  /** フォントサイズ（px）。省略時は12。ダッシュボードで詳細と揃えるなら 12 */
  fontSize?: number;
  /** true のとき、Y軸の左端から表示（余白を項目名で使う）。省略時は従来どおり右寄せ */
  alignLeftInAxis?: boolean;
}) => {
  const { x = 0, y = 0, payload, width: axisWidth, labelWidth: labelWidthProp, fontSize = 12, alignLeftInAxis = false } = props;
  const text = payload?.value != null && payload?.value !== "" ? String(payload.value) : "";
  const pixelWidth = labelWidthProp ?? (axisWidth != null ? axisWidth - 8 : null);
  const lines =
    pixelWidth != null && pixelWidth > 0 && text
      ? splitLabelByWidth(text, pixelWidth)
      : text ? [text] : [];
  const lineHeight = lineHeightForFontSize(fontSize);
  const textX = alignLeftInAxis && axisWidth != null && axisWidth > 0 ? x - axisWidth + 10 : x;
  const textAnchor = alignLeftInAxis ? "start" : "end";

  return (
    <g x={x} y={y}>
      {lines.map((line, index) => (
        <text
          key={index}
          x={textX}
          y={y - (lines.length - 2) * (lineHeight / 2) + index * lineHeight}
          dy="-4"
          fontSize={fontSize}
          textAnchor={textAnchor}
          fill="#666"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

interface ChartsProps {
  getGraphData: (edit: boolean) => void;
  fetchData: dataDifine[];
  fetchAllData: dataDifine[];
  fetchColumn: string[];
  tableHeight: string;
  tableTitle: string;
  /** モーダル用: タイトルと更新ボタンを行ごと非表示にする */
  hideTitleAndUpdate?: boolean;
  /** メインとサブを横並び(false)か縦並び(true)にする。縦並び時はサブの高さが行数に応じて可変 */
  verticalLayout?: boolean;
}

/** サブグラフの1行あたりの高さ（文字つぶれ防止のため多め） */
const ROW_HEIGHT_PX = 40;
const SUB_CHART_MIN_HEIGHT_PX = 220;
const MAIN_CHART_HEIGHT_PX = 260;

/** 項目が少ないときも見やすいよう、最低この行数ぶんの高さは確保する */
const SUB_CHART_MIN_ROWS = 5;

export function Charts(props: ChartsProps) {
  const {
    getGraphData,
    fetchData,
    fetchAllData,
    fetchColumn,
    tableHeight,
    tableTitle,
    hideTitleAndUpdate = false,
    verticalLayout = false,
  } = props;
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  /** 行数に応じて縦に伸ばす。項目が少ないときも最低 MIN_ROWS 分の高さを確保 */
  const subChartHeight = verticalLayout
    ? Math.max(
        SUB_CHART_MIN_HEIGHT_PX,
        Math.max(fetchAllData.length, SUB_CHART_MIN_ROWS) * ROW_HEIGHT_PX,
      )
    : undefined;

  const chartContent = (
    <>
      <Box
        sx={{
          height: verticalLayout ? MAIN_CHART_HEIGHT_PX : "100%",
          width: verticalLayout ? "100%" : "calc(100% - 250px)",
          ...(verticalLayout ? { flexShrink: 0 } : {}),
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={500}
            height={300}
            data={fetchData}
            margin={{
              top: 10,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 9999 }}
            />
            {fetchColumn.map((column, i) => {
              return (
                <Bar
                  key={column}
                  dataKey={column}
                  stackId="a"
                  fill={colorPlette[i % colorPlette.length]}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {fetchAllData.length > 0 && (
      <Box
        sx={{
          height: verticalLayout ? undefined : "100%",
          width: verticalLayout ? "100%" : "250px",
          ...(verticalLayout
            ? {
                mt: 3,
                flex: "1 1 0",
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                pr: 2,
              }
            : {}),
          ...(verticalLayout ? {} : { height: "100%" }),
        }}
      >
        <Box sx={{ ...(verticalLayout ? { height: subChartHeight, minHeight: SUB_CHART_MIN_HEIGHT_PX } : { height: "100%" }) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
            width={500}
            height={300}
            data={fetchAllData}
            margin={{
              top: 20,
              right: 24,
              left: 8,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <YAxis
              type="category"
              dataKey="name"
              tick={(tickProps) => (
                <CustomizedYAxisTick
                  {...tickProps}
                  labelWidth={(verticalLayout ? 220 : 100) - 8}
                />
              )}
              width={verticalLayout ? 220 : 100}
            />
            <XAxis type="number" />
            <Tooltip
              content={<CustomTooltipAll />}
              wrapperStyle={{ zIndex: 9999 }}
            />
            <Bar dataKey="合計" stackId="a">
              {fetchAllData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colorPlette[index % colorPlette.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </Box>
      </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: verticalLayout ? "100%" : tableHeight,
        minHeight: verticalLayout
          ? (fetchAllData.length > 0 ? MAIN_CHART_HEIGHT_PX + SUB_CHART_MIN_HEIGHT_PX : MAIN_CHART_HEIGHT_PX)
          : undefined,
        display: "flex",
        flexDirection: verticalLayout ? "column" : "row",
      }}
    >
      {!hideTitleAndUpdate && (
        <Typography
          variant="h6"
          gutterBottom
          component="div"
          position={isSmartPhone ? "fixed" : "static"}
          style={{
            width: "100%",
            top: isSmartPhone ? "80px" : undefined,
            backgroundColor: "white",
            paddingTop: isSmartPhone ? "8px" : undefined,
            paddingBottom: isSmartPhone ? "8px" : undefined,
            zIndex: isSmartPhone ? 1 : undefined,
          }}
        >
          <Box
            className="shippori-antique-regular"
            sx={{
              display: "inline",
              fontSize: "16px",
              marginLeft: isSmartPhone ? "10px" : undefined,
            }}
          >
            {tableTitle}
            <IconButton
              sx={{ height: "25px", width: "25px", marginLeft: "5px" }}
              aria-label="detail row"
              size="small"
              onClick={() => getGraphData(false)}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Box>
        </Typography>
      )}
      <Box
        sx={{
          height: hideTitleAndUpdate ? "100%" : "calc(100% - 32px)",
          width: "100%",
          display: "flex",
          flexDirection: verticalLayout ? "column" : "row",
          ...(verticalLayout ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        {chartContent}
      </Box>
    </Box>
  );
}
